"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSession } from "@/hooks/useSession";
import { useAuth } from "@/contexts/AuthContext";
import {
  useSkillFiles,
  type SkillFile,
  type SkillFileInfo,
} from "@/hooks/useSkillFiles";
import { getSkillBrandColor } from "./SkillCard";
import { ProviderBadge } from "./ProviderBadge";
import { buildFileTree, formatFileSize } from "./SkillFileViewer";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { useToast } from "@/components/ui/Toast";
import { resilientFetch } from "@/lib/fetch";
import { getItemBySlug, forkItem } from "@/lib/api/items";
import { installSkill } from "@/lib/api/skills";
import { captureV1 } from "@/lib/analytics-v1";
import { ANALYTICS_EVENTS } from "@bash/shared";
import type { ItemDetail, SkillDefinition, SkillSummary } from "@bash/shared";
import { SCAFFOLD_FOLDERS, deriveProvider } from "./types";
import type { TreeNode } from "./types";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import Masthead from "@/components/atoms/masthead";
import BackButton from "@/components/atoms/back-button";
import { PencilIcon } from "@/components/icons/PencilIcon";
import { SkillCopyIcon } from "@/components/icons/SkillCopyIcon";
import { SkillCheckSmallIcon } from "@/components/icons/SkillCheckSmallIcon";
import { FolderIcon } from "@/components/icons/FolderIcon";
import { SkillFileTextIcon } from "@/components/icons/SkillFileTextIcon";
import { SkillTrashIcon } from "@/components/icons/SkillTrashIcon";
import { TrashSmallIcon } from "@/components/icons/TrashSmallIcon";
import { SkillPlusIcon } from "@/components/icons/SkillPlusIcon";
import { WebSearchIcon } from "@/components/icons/WebSearchIcon";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseFrontmatter(raw: string): {
  frontmatter: string | null;
  body: string;
} {
  const m = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!m) return { frontmatter: null, body: raw };
  return { frontmatter: m[1].trim(), body: m[2].trim() };
}

function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function cleanName(name: string, slug: string): string {
  const n = name || slug;
  if (n.includes("-") && !n.includes(" ")) return slugToTitle(n);
  return n;
}

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function colorTint(hex: string, alpha: number): string {
  if (hex.startsWith("#") && hex.length >= 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(120, 120, 120, ${alpha})`;
}

function isMarkdownFile(path: string): boolean {
  return /\.(md|mdx|markdown)$/i.test(path);
}

// ---------------------------------------------------------------------------
// Recursive file tree node
// ---------------------------------------------------------------------------

interface FileNodeProps {
  node: TreeNode;
  depth: number;
  selectedPath: string;
  onSelect: (path: string) => void;
  brandColor: string;
  editing?: boolean;
  pendingDeletes?: Set<string>;
  pendingNewFiles?: Map<string, string>;
  onDeleteFile?: (path: string) => void;
  onUndoDelete?: (path: string) => void;
  // Inline "new file" input state — lifted to parent so it renders inside the correct folder
  newFileTarget?: string | null;
  newFileName?: string;
  onSetNewFileTarget?: (folderPath: string | null) => void;
  onSetNewFileName?: (name: string) => void;
  onConfirmNewFile?: () => void;
}

function FileNode({
  node,
  depth,
  selectedPath,
  onSelect,
  brandColor,
  editing,
  pendingDeletes,
  pendingNewFiles,
  onDeleteFile,
  onUndoDelete,
  newFileTarget,
  newFileName,
  onSetNewFileTarget,
  onSetNewFileName,
  onConfirmNewFile,
}: FileNodeProps) {
  const [expanded, setExpanded] = useState(false);

  const sortedChildren = useMemo(() => {
    return Array.from(node.children.values()).sort((a, b) => {
      // SKILL.md always first
      if (a.name === "SKILL.md") return -1;
      if (b.name === "SKILL.md") return 1;
      // Files before folders
      if (a.isDir !== b.isDir) return a.isDir ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  }, [node.children]);

  const padLeft = 8 + depth * 20;
  const childDepth = node.name ? depth + 1 : depth;

  // Check if the inline new-file input targets this folder
  // Skip root node (empty path) — renderTreeItems handles root-level input separately
  const isNewFileTarget = editing && node.name && newFileTarget === node.path;

  // Directory
  if (node.isDir) {
    const isDeleted = pendingDeletes?.has(node.path);

    return (
      <div>
        {node.name && (
          <div className="group/dir flex items-center">
            <button
              className={cn(
                "flex-1 flex items-center gap-1.5 py-1.5 px-2 rounded-12 text-left text-3 text-fg-muted hover:bg-bg-muted/50 transition-colors cursor-pointer",
                isDeleted ? "opacity-40 line-through" : ""
              )}
              style={{ paddingLeft: padLeft }}
              onClick={() => setExpanded(!expanded)}
            >
              <span className="text-2.5 text-fg-faint w-2.5 flex-shrink-0 font-mono text-4">
                {expanded ? "\u25BE" : "\u25B8"}
              </span>
              <FolderIcon open={expanded} />
              <span className="truncate">{node.name}/</span>
            </button>
            {editing && onSetNewFileTarget && (
              <button
                className="opacity-0 group-hover/dir:opacity-100 p-1 mr-1 rounded text-fg-faint hover:text-fg-muted transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(true);
                  onSetNewFileTarget(node.path);
                }}
                title={`Add file to ${node.name}/`}
              >
                <SkillPlusIcon />
              </button>
            )}
          </div>
        )}
        {(!node.name || expanded) && (
          <>
            {sortedChildren.map((child) => (
              <FileNode
                key={child.path}
                node={child}
                depth={childDepth}
                selectedPath={selectedPath}
                onSelect={onSelect}
                brandColor={brandColor}
                editing={editing}
                pendingDeletes={pendingDeletes}
                pendingNewFiles={pendingNewFiles}
                onDeleteFile={onDeleteFile}
                onUndoDelete={onUndoDelete}
                newFileTarget={newFileTarget}
                newFileName={newFileName}
                onSetNewFileTarget={onSetNewFileTarget}
                onSetNewFileName={onSetNewFileName}
                onConfirmNewFile={onConfirmNewFile}
              />
            ))}
            {/* Inline new-file input — renders inside this folder */}
            {isNewFileTarget &&
              onSetNewFileName &&
              onConfirmNewFile &&
              onSetNewFileTarget && (
                <div
                  className="flex items-center gap-1 py-1 px-2"
                  style={{ paddingLeft: 8 + childDepth * 20 }}
                >
                  <SkillFileTextIcon />
                  <input
                    className="flex-1 text-3 bg-transparent border-b border-border-default text-fg-default outline-none px-1 py-0.5"
                    value={newFileName || ""}
                    onChange={(e) => onSetNewFileName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (newFileName || "").trim())
                        onConfirmNewFile();
                      if (e.key === "Escape") onSetNewFileTarget(null);
                    }}
                    placeholder="filename.md"
                    autoFocus
                  />
                  <button
                    className="text-2.5 text-fg-muted hover:text-fg-default px-1 cursor-pointer disabled:opacity-40"
                    onClick={onConfirmNewFile}
                    disabled={!(newFileName || "").trim()}
                  >
                    Add
                  </button>
                  <button
                    className="text-2.5 text-fg-faint hover:text-fg-muted px-1 cursor-pointer"
                    onClick={() => onSetNewFileTarget(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}
          </>
        )}
      </div>
    );
  }

  // File
  const isActive = selectedPath === node.path;
  const isDeleted = pendingDeletes?.has(node.path);
  const isNew = pendingNewFiles?.has(node.path);
  const isScaffold = !node.file && !isNew; // scaffold nodes have no file metadata and aren't pending new

  return (
    <div className="group/file flex items-center">
      <button
        className={cn(
          "flex-1 flex items-center gap-1.5 py-1.5 px-2 rounded-12 text-left text-3 transition-colors cursor-pointer",
          isDeleted ? "opacity-40 line-through" : ""
        )}
        style={{
          paddingLeft: padLeft,
          background:
            isActive && !isDeleted
              ? colorTint(brandColor, 0.08)
              : "transparent",
          color: isActive && !isDeleted ? brandColor : "var(--fg-muted)",
          fontWeight: isActive && !isDeleted ? 600 : 400,
        }}
        onClick={() => {
          if (!isDeleted) onSelect(node.path);
        }}
        onMouseEnter={(e) => {
          if (!isActive && !isDeleted)
            e.currentTarget.style.background = "var(--bg-muted)";
        }}
        onMouseLeave={(e) => {
          if (!isActive || isDeleted)
            e.currentTarget.style.background = "transparent";
        }}
      >
        <SkillFileTextIcon />
        <span className="truncate flex-1">{node.name}</span>
        {isNew && (
          <span className="text-2.5 text-green-500 flex-shrink-0 font-medium">
            new
          </span>
        )}
        {!isNew && node.file?.sizeBytes != null && (
          <span className="text-2.5 text-fg-faint flex-shrink-0">
            {formatFileSize(node.file.sizeBytes)}
          </span>
        )}
      </button>
      {editing && isDeleted && onUndoDelete && (
        <button
          className="text-2.5 text-fg-muted hover:text-fg-default px-1 mr-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onUndoDelete(node.path);
          }}
          title="Undo delete"
        >
          undo
        </button>
      )}
      {editing &&
        !isDeleted &&
        !isScaffold &&
        onDeleteFile &&
        node.name !== "SKILL.md" && (
          <button
            className="opacity-0 group-hover/file:opacity-100 p-1 mr-1 rounded text-fg-faint hover:text-status-error transition-all cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFile(node.path);
            }}
            title="Delete file"
          >
            <TrashSmallIcon />
          </button>
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface SkillResumePageProps {
  slug: string;
}

export function SkillResumePage({ slug }: SkillResumePageProps) {
  const router = useRouter();
  const { session } = useSession();
  const { user } = useAuth();
  const { toast } = useToast();

  const isNew = slug === "new";
  const backUrl = session ? "/library/skills" : "/skills";

  const [name, setName] = useState("");
  const computedSlug = isNew ? nameToSlug(name) || "new-skill" : slug;

  // Fetch state
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [skill, setSkill] = useState<Partial<SkillDefinition> | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [notFound, setNotFound] = useState(false);

  // Editability of the loaded item (owned → true, system/marketplace → false)
  const [editable, setEditable] = useState(isNew);

  // Edit state
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");

  // Delete
  const [deleting, setDeleting] = useState(false);

  // Install / Fork (read-only items)
  const [installing, setInstalling] = useState(false);
  const [forking, setForking] = useState(false);

  // Copy
  const [copied, setCopied] = useState(false);

  // File explorer
  const [selectedPath, setSelectedPath] = useState<string>("SKILL.md");

  // In-memory file store (reference/extra files only — never SKILL.md).
  // Seeded from the loaded item's inline `files` bundle, persisted via PATCH.
  const { files, reset: resetFiles, getContent } = useSkillFiles();

  // === Batched edit state (all local until Save) ===
  const [fileEdits, setFileEdits] = useState<Map<string, string>>(new Map()); // path -> edited content
  const [pendingNewFiles, setPendingNewFiles] = useState<Map<string, string>>(
    new Map()
  ); // path -> content (new files)
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set()); // paths to delete on save
  // Inline new-file input
  const [newFileTarget, setNewFileTarget] = useState<string | null>(null); // folder path (or '' for root)
  const [newFileName, setNewFileName] = useState("");

  // Creator
  const creator = useMemo(
    () => ({
      name:
        user?.user_metadata?.full_name || user?.user_metadata?.name || "You",
      avatarUrl:
        user?.user_metadata?.avatar_url || user?.user_metadata?.picture,
    }),
    [user]
  );

  const buildHeaders = useCallback(() => {
    const headers: Record<string, string> = {};
    if (session?.access_token)
      headers["Authorization"] = `Bearer ${session.access_token}`;
    return headers;
  }, [session?.access_token]);

  // Hydrate component state from a loaded ItemDetail.
  const hydrateFromItem = useCallback(
    (it: ItemDetail) => {
      setItem(it);
      setEditable(it.editable);
      // Keep a SkillDefinition-ish projection for the existing header/meta UI.
      setSkill({
        name: it.name,
        description: it.description,
        content: it.content,
        category: it.category ?? undefined,
        icon: it.icon ?? undefined,
        color: it.color ?? undefined,
        mcps: it.mcps,
      } as Partial<SkillDefinition>);
      setName(cleanName(it.name || "", slug));
      setDescription(it.description || "");
      setContent(it.content || "");
      // Seed the in-memory file store (reference files only — never SKILL.md).
      resetFiles((it.files || []).filter((f) => f.path !== "SKILL.md"));
    },
    [slug, resetFiles]
  );

  // Load skill detail via the items API
  useEffect(() => {
    if (isNew || !session?.access_token) return;
    let cancelled = false;
    setLoading(true);
    getItemBySlug("skill", slug, session.access_token)
      .then((it) => {
        if (cancelled) return;
        if (!it) {
          setNotFound(true);
        } else {
          hydrateFromItem(it);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, session?.access_token, hydrateFromItem]);

  const provider = deriveProvider(computedSlug, skill?.icon);

  const skillSummary = useMemo(
    (): SkillSummary => ({
      slug: computedSlug,
      name: name || slugToTitle(computedSlug),
      description: description || "",
      category: skill?.category || "general",
      icon: skill?.icon,
      color: skill?.color,
      mcps: skill?.mcps || [],
    }),
    [computedSlug, name, description, skill]
  );

  const brandColor = useMemo(
    () => getSkillBrandColor(skillSummary),
    [skillSummary]
  );

  // Build real tree from file API + pending new files
  // - Skills with just SKILL.md: inject scaffold folders so users learn the structure
  // - Skills with their own folder structure: show as-is
  const fileTree = useMemo(() => {
    // Adapt the lean in-memory {path, content} bundle to the SkillFileInfo
    // shape buildFileTree expects. Only `path` is read by the builder; the
    // remaining metadata is filled with neutral defaults (sizeBytes=null so
    // the tree omits the size pill for inline files).
    const treeInput: SkillFileInfo[] = files.map((f: SkillFile) => ({
      id: f.path,
      path: f.path,
      filename: f.path.split("/").pop() || f.path,
      contentType: "text/plain",
      sizeBytes: null,
      isBinary: false,
      checksum: null,
    }));
    const tree =
      treeInput.length > 0
        ? buildFileTree(treeInput)
        : {
            name: "",
            path: "",
            isDir: true,
            children: new Map<string, TreeNode>(),
          };
    // Always ensure SKILL.md node exists
    if (!tree.children.has("SKILL.md")) {
      tree.children.set("SKILL.md", {
        name: "SKILL.md",
        path: "SKILL.md",
        isDir: false,
        children: new Map(),
      });
    }
    // Only inject scaffold folders if the skill has NO existing directories
    const hasAnyDir = Array.from(tree.children.values()).some((n) => n.isDir);
    if (!hasAnyDir) {
      for (const folder of SCAFFOLD_FOLDERS) {
        tree.children.set(folder, {
          name: folder,
          path: folder,
          isDir: true,
          children: new Map(),
        });
      }
    }
    // Inject pending new files into the tree so they appear immediately
    for (const [path] of pendingNewFiles) {
      const parts = path.split("/");
      let current = tree;
      for (let i = 0; i < parts.length - 1; i++) {
        const dirName = parts[i];
        if (!current.children.has(dirName)) {
          const dirPath = parts.slice(0, i + 1).join("/");
          current.children.set(dirName, {
            name: dirName,
            path: dirPath,
            isDir: true,
            children: new Map(),
          });
        }
        current = current.children.get(dirName)!;
      }
      const fileName = parts[parts.length - 1];
      if (!current.children.has(fileName)) {
        current.children.set(fileName, {
          name: fileName,
          path,
          isDir: false,
          children: new Map(),
        });
      }
    }
    return tree;
  }, [files, pendingNewFiles]);

  // Handle file selection — content is in memory, no fetch needed.
  const handleFileSelect = useCallback((path: string) => {
    setSelectedPath(path);
  }, []);

  // Get the effective content for a file (edited version, pending new, or original)
  const getEffectiveContent = useCallback(
    (path: string): string => {
      if (pendingNewFiles.has(path)) return pendingNewFiles.get(path)!;
      if (fileEdits.has(path)) return fileEdits.get(path)!;
      if (path === "SKILL.md") return content;
      return getContent(path) ?? "";
    },
    [pendingNewFiles, fileEdits, content, getContent]
  );

  // Track file content edit
  const handleFileContentEdit = useCallback(
    (path: string, newContent: string) => {
      if (path === "SKILL.md") {
        setContent(newContent);
      } else if (pendingNewFiles.has(path)) {
        setPendingNewFiles((prev) => {
          const n = new Map(prev);
          n.set(path, newContent);
          return n;
        });
      } else {
        setFileEdits((prev) => {
          const n = new Map(prev);
          n.set(path, newContent);
          return n;
        });
      }
    },
    [pendingNewFiles]
  );

  // Stage a file for deletion (local only — persisted on Save)
  const handleDeleteFile = useCallback(
    (path: string) => {
      // If it's a pending new file, just remove it entirely
      if (pendingNewFiles.has(path)) {
        setPendingNewFiles((prev) => {
          const n = new Map(prev);
          n.delete(path);
          return n;
        });
        if (selectedPath === path) setSelectedPath("SKILL.md");
        return;
      }
      setPendingDeletes((prev) => new Set(prev).add(path));
      if (selectedPath === path) setSelectedPath("SKILL.md");
    },
    [pendingNewFiles, selectedPath]
  );

  // Undo a staged delete
  const handleUndoDelete = useCallback((path: string) => {
    setPendingDeletes((prev) => {
      const n = new Set(prev);
      n.delete(path);
      return n;
    });
  }, []);

  // Stage a new file (local only — persisted on Save)
  const handleConfirmNewFile = useCallback(() => {
    if (!newFileName.trim() || newFileTarget === null) return;
    const filePath = newFileTarget
      ? `${newFileTarget}/${newFileName.trim()}`
      : newFileName.trim();
    setPendingNewFiles((prev) => {
      const n = new Map(prev);
      n.set(filePath, "");
      return n;
    });
    setNewFileTarget(null);
    setNewFileName("");
    setSelectedPath(filePath);
  }, [newFileName, newFileTarget]);

  // Handlers
  const resetEditState = useCallback(() => {
    setFileEdits(new Map());
    setPendingNewFiles(new Map());
    setPendingDeletes(new Set());
    setNewFileTarget(null);
    setNewFileName("");
  }, []);

  const handleEdit = useCallback(() => {
    setEditing(true);
    setError(null);
    resetEditState();
  }, [resetEditState]);

  const handleCancel = useCallback(() => {
    if (isNew) {
      router.push(backUrl);
      return;
    }
    if (item) {
      setName(cleanName(item.name || "", slug));
      setDescription(item.description || "");
      setContent(item.content || "");
      resetFiles((item.files || []).filter((f) => f.path !== "SKILL.md"));
    }
    setEditing(false);
    setError(null);
    resetEditState();
  }, [item, slug, resetFiles, resetEditState]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    if (!isNew && !item) return;
    setSaving(true);
    setError(null);
    try {
      // Build the FULL inline file bundle from the in-memory store + the
      // batched local edits (new files, content edits, deletes). SKILL.md is
      // NEVER part of `files` — it's the primary `content` field, and the
      // server rejects it inside the bundle.
      let bundle: SkillFile[] = files
        .filter((f) => !pendingDeletes.has(f.path))
        .map((f) => ({
          path: f.path,
          content: fileEdits.has(f.path) ? fileEdits.get(f.path)! : f.content,
        }));
      for (const [path, fileContent] of pendingNewFiles.entries()) {
        if (path === "SKILL.md") continue;
        bundle = bundle.filter((f) => f.path !== path);
        bundle.push({ path, content: fileContent });
      }

      // Merge into the real metadata object so a save preserves
      // category/icon/color/mcps/tools (the server replaces the column wholesale).
      const existingMetadata = item?.metadata ?? {};
      let res;

      if (isNew) {
        const newSlug = nameToSlug(name);
        if (!newSlug) {
          setError("Invalid skill name");
          setSaving(false);
          return;
        }
        res = await resilientFetch(`${SERVER_URL}/api/items/skill`, {
          method: "POST",
          headers: { ...buildHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            slug: newSlug,
            content: content.trim(),
            metadata: {
              ...existingMetadata,
              name: name.trim(),
              description: description.trim(),
            },
            files: bundle,
          }),
        });
      } else {
        res = await resilientFetch(
          `${SERVER_URL}/api/items/skill/${item!.id}`,
          {
            method: "PATCH",
            headers: { ...buildHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              content: content.trim(),
              metadata: {
                ...existingMetadata,
                name: name.trim(),
                description: description.trim(),
              },
              files: bundle,
            }),
          }
        );
      }

      if (!res.ok) {
        let msg = "Failed to save";
        try {
          msg = (await res.json()).error || msg;
        } catch {
          /* ignore */
        }
        setError(msg);
        setSaving(false);
        return;
      }

      // The PATCH persisted. Exit edit mode immediately so a refetch failure
      // below can't surface a misleading "something went wrong" after success.
      resetEditState();
      setEditing(false);

      if (isNew) {
        const json = await res.json();
        router.push(`/skills/${json.slug || nameToSlug(name)}`);
        return;
      }

      // Best-effort re-load to refresh from the server's canonical projection.
      try {
        const refreshed = await getItemBySlug(
          "skill",
          slug,
          session?.access_token || ""
        );
        if (refreshed) hydrateFromItem(refreshed);
      } catch {
        /* save already succeeded; ignore refetch failure */
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setSaving(false);
  }, [
    isNew,
    item,
    name,
    description,
    content,
    slug,
    files,
    fileEdits,
    pendingNewFiles,
    pendingDeletes,
    buildHeaders,
    session?.access_token,
    hydrateFromItem,
    resetEditState,
    router,
  ]);

  const handleDelete = useCallback(async () => {
    if (!item) return;
    setDeleting(true);
    try {
      const res = await resilientFetch(
        `${SERVER_URL}/api/items/skill/${item.id}`,
        { method: "DELETE", headers: buildHeaders() }
      );
      if (res.ok) router.push(backUrl);
      else {
        setError("Failed to delete");
        setDeleting(false);
      }
    } catch {
      setError("Failed to delete");
      setDeleting(false);
    }
  }, [item, buildHeaders, router]);

  const handleInstall = useCallback(async () => {
    setInstalling(true);
    setError(null);
    try {
      await installSkill(slug);
      captureV1(ANALYTICS_EVENTS.SKILL_INSTALLED, {
        skill_slug: slug,
        source: "library",
        surface: "skill_detail",
      });
      toast("Skill installed");
    } catch {
      setError("Failed to install");
    }
    setInstalling(false);
  }, [slug, toast]);

  const handleFork = useCallback(async () => {
    if (!item) return;
    setForking(true);
    setError(null);
    try {
      const result = await forkItem("skill", item.id);
      toast(`Forked to “${result.slug}”`);
    } catch {
      setError("Failed to fork");
    }
    setForking(false);
  }, [item, toast]);

  // TODO(publish): re-add publish/unpublish via items publish endpoints.

  const copyCommand = useCallback(() => {
    navigator.clipboard.writeText(`/${computedSlug}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [computedSlug]);

  // =========================================================================
  // Loading
  // =========================================================================
  if (loading) {
    const sk = { background: "var(--bg-muted)" };
    return (
      <div className="skill-page contents">
        <div className="flex flex-col gap-y-3">
          <BackButton title="Skills" slug={backUrl} />

          {/* Header skeleton */}
          <div className="flex items-start gap-4 min-w-0 max-sm:gap-3">
            <div
              className="animate-pulse-subtle-1 rounded-12 shrink-0 w-[48px] h-[48px]"
              style={{ ...sk }}
            />
            <div className="flex-1 space-y-2.5">
              <div
                className="animate-pulse-subtle-1 rounded-12 w-[200px] max-w-[60%] h-[22px]"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 rounded w-[80%] h-[14px]"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 rounded w-[200px] max-w-[50%] h-[14px]"
                style={{ ...sk }}
              />
            </div>
            <div className="flex gap-2">
              <div
                className="animate-pulse-subtle-1 rounded-12 w-[60px] h-[32px]"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 rounded-12 w-[48px] h-[32px]"
                style={{ ...sk }}
              />
            </div>
          </div>
        </div>

        {/* Explorer skeleton */}
        <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden max-md:flex-col rounded-16 border border-border-default min-h-[300px]">
          <div className="shrink-0 w-55 overflow-y-auto overflow-x-hidden bg-bg-subtle max-md:w-full max-md:max-h-55 max-md:border-b border-border-default max-sm:max-h-45 p-4 space-y-2.5">
            <div
              className="animate-pulse-subtle-1 rounded ml-1 w-[130px] max-w-[80%] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded ml-1 w-[80px] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded ml-1 w-[90px] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded ml-1 w-[70px] h-[14px]"
              style={{ ...sk }}
            />
          </div>
          <div className="flex-1 min-w-0 overflow-auto [overflow-wrap:break-word] [word-break:break-word] p-5 max-md:p-4 max-sm:p-3 space-y-3">
            <div
              className="animate-pulse-subtle-1 rounded w-[120px] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded w-[100%] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded w-[90%] h-[14px]"
              style={{ ...sk }}
            />
            <div
              className="animate-pulse-subtle-1 rounded w-[85%] h-[14px]"
              style={{ ...sk }}
            />
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Not found
  // =========================================================================
  if (notFound) {
    return (
      <div className="skill-page contents">
        <BackButton title="Skills" slug={backUrl} />

        <div className="text-center" style={{ padding: "4rem 2rem" }}>
          <div className="w-14 h-14 rounded-20 bg-bg-muted flex items-center justify-center mx-auto mb-4">
            <WebSearchIcon
              width={24}
              height={24}
              stroke="var(--fg-faint)"
              strokeWidth="1.5"
            />
          </div>
          <p className="text-4 text-fg-muted mb-4">Skill not found</p>
          <Button asChild>
            <Link href={backUrl}>Back to Skills</Link>
          </Button>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Main
  // =========================================================================

  const displayedName = cleanName(name, computedSlug);

  // Build breadcrumb for selected path
  const breadcrumbParts = selectedPath.split("/");

  // Content panel renderer
  function renderContentPanel() {
    // Edit mode — textarea for any file
    if (editing) {
      const editValue = getEffectiveContent(selectedPath);
      return (
        <textarea
          className="w-full [font-family:'OpenAI_Sans',_system-ui,_sans-serif] text-3.5 leading-[1.7] text-fg-default border border-border-default py-4 px-5 [resize:vertical] outline-none transition-colors duration-200 focus:border-border-strong rounded-none bg-transparent min-h-[100%]"
          value={editValue}
          onChange={(e) => handleFileContentEdit(selectedPath, e.target.value)}
          placeholder={
            selectedPath === "SKILL.md"
              ? "Write the skill instructions..."
              : `Edit ${selectedPath}...`
          }
          disabled={saving}
          style={{
            border: "none",
          }}
        />
      );
    }

    // View mode: SKILL.md — use inline content
    if (selectedPath === "SKILL.md") {
      if (!content)
        return (
          <p className="text-3.5 text-fg-faint italic">
            No instructions yet. Click Edit to add some.
          </p>
        );
      const { frontmatter, body } = parseFrontmatter(content);
      return (
        <div className="text-3.5 leading-[1.7] text-fg-default">
          {frontmatter && (
            <div className="mb-4 rounded-12 border border-border-subtle bg-bg-subtle overflow-hidden">
              <div className="px-3 py-1.5 border-b border-border-subtle">
                <span className="text-2.5 font-medium uppercase tracking-wider text-fg-subtle">
                  Frontmatter
                </span>
              </div>
              <pre className="px-3 py-2 text-3 font-mono text-4 text-fg-muted whitespace-pre-wrap m-0 leading-relaxed">
                {frontmatter}
              </pre>
            </div>
          )}
          <MarkdownRenderer content={body} />
        </div>
      );
    }

    // Other files — from the in-memory store
    const fileContent = getContent(selectedPath);

    if (fileContent === undefined || fileContent === null) {
      return (
        <p className="text-3.5 text-fg-faint italic">
          Unable to load file content.
        </p>
      );
    }

    // Markdown files
    if (isMarkdownFile(selectedPath)) {
      const { frontmatter, body } = parseFrontmatter(fileContent);
      return (
        <div className="text-3.5 leading-[1.7] text-fg-default">
          {frontmatter && (
            <div className="mb-4 rounded-12 border border-border-subtle bg-bg-subtle overflow-hidden">
              <div className="px-3 py-1.5 border-b border-border-subtle">
                <span className="text-2.5 font-medium uppercase tracking-wider text-fg-subtle">
                  Frontmatter
                </span>
              </div>
              <pre className="px-3 py-2 text-3 font-mono text-4 text-fg-muted whitespace-pre-wrap m-0 leading-relaxed">
                {frontmatter}
              </pre>
            </div>
          )}
          <MarkdownRenderer content={body} />
        </div>
      );
    }

    // Non-markdown (code, json, etc.)
    return (
      <pre className="text-3 text-fg-default font-mono text-4 whitespace-pre-wrap break-words leading-relaxed m-0">
        {fileContent}
      </pre>
    );
  }

  // Tree panel content
  function renderTreeItems() {
    // Real file tree from the in-memory store
    if (fileTree) {
      return (
        <div className="px-1 pb-3">
          <FileNode
            node={fileTree}
            depth={0}
            selectedPath={selectedPath}
            onSelect={handleFileSelect}
            brandColor={brandColor}
            editing={editing}
            pendingDeletes={pendingDeletes}
            pendingNewFiles={pendingNewFiles}
            onDeleteFile={editing ? handleDeleteFile : undefined}
            onUndoDelete={editing ? handleUndoDelete : undefined}
            newFileTarget={newFileTarget}
            newFileName={newFileName}
            onSetNewFileTarget={editing ? setNewFileTarget : undefined}
            onSetNewFileName={editing ? setNewFileName : undefined}
            onConfirmNewFile={editing ? handleConfirmNewFile : undefined}
          />

          {/* "New file" at root level (edit mode, only when no inline input is active) */}
          {editing && newFileTarget === null && (
            <button
              className="w-full flex items-center gap-1.5 py-1.5 px-2 mt-1 rounded-12 text-left text-3 text-fg-faint hover:text-fg-muted hover:bg-bg-muted/50 transition-colors cursor-pointer pl-2"
              onClick={() => {
                setNewFileTarget("");
                setNewFileName("");
              }}
            >
              <SkillPlusIcon />
              <span>New file</span>
            </button>
          )}

          {/* Inline input for root-level new file (target === '') */}
          {editing && newFileTarget === "" && (
            <div className="flex items-center gap-1 py-1 px-2 pl-2">
              <SkillFileTextIcon />
              <input
                className="flex-1 text-3 bg-transparent border-b border-border-default text-fg-default outline-none px-1 py-0.5"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFileName.trim())
                    handleConfirmNewFile();
                  if (e.key === "Escape") setNewFileTarget(null);
                }}
                placeholder="filename.md"
                autoFocus
              />
              <button
                className="text-2.5 text-fg-muted hover:text-fg-default px-1 cursor-pointer disabled:opacity-40"
                onClick={handleConfirmNewFile}
                disabled={!newFileName.trim()}
              >
                Add
              </button>
              <button
                className="text-2.5 text-fg-faint hover:text-fg-muted px-1 cursor-pointer"
                onClick={() => setNewFileTarget(null)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      );
    }

    // Fallback: minimal tree with just SKILL.md
    const isActive = selectedPath === "SKILL.md";
    return (
      <div className="px-1 pb-3">
        <button
          className="w-full flex items-center gap-1.5 py-1.5 px-2 rounded-12 text-left text-3 transition-colors cursor-pointer pl-2"
          style={{
            background: isActive ? colorTint(brandColor, 0.08) : "transparent",
            color: isActive ? brandColor : "var(--fg-muted)",
            fontWeight: isActive ? 600 : 400,
          }}
          onClick={() => handleFileSelect("SKILL.md")}
        >
          <SkillFileTextIcon />
          <span className="truncate">SKILL.md</span>
        </button>
      </div>
    );
  }

  return (
    <div className="skill-page contents">
      <header className="flex flex-col gap-y-3">
        <BackButton title="Skills" slug={backUrl} />

        <Masthead
          title={
            editing ? (
              <input
                className="w-full max-w-100 font-bold bg-transparent border border-dashed border-border-muted rounded-10 py-1.5 px-3 text-fg-default outline-none transition-colors duration-200 focus:border-border-strong focus:[border-style:solid] text-left text-4.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Skill Name"
                autoFocus
                disabled={saving}
                style={{
                  padding: "4px 8px",
                }}
              />
            ) : (
              displayedName
            )
          }
          subtitle={
            editing ? (
              <input
                className="w-full max-w-200 text-3.5 bg-transparent border border-dashed border-border-muted rounded-8 py-1.5 px-3 text-fg-muted outline-none transition-colors duration-200 focus:border-border-strong focus:[border-style:solid] max-sm:text-3.5 text-left"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this skill does..."
                disabled={saving}
              />
            ) : (
              description
            )
          }
          actions={
            editing ? (
              <>
                <Button
                  variant="secondary"
                  disabled={saving}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  className="gap-1.5"
                  disabled={saving || !name.trim() || !description.trim()}
                  onClick={handleSave}
                >
                  {saving && (
                    <span className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin-1" />
                  )}
                  {isNew ? "Create Skill" : "Save"}
                </Button>
              </>
            ) : editable ? (
              <Button className="gap-1.5" onClick={handleEdit}>
                <PencilIcon /> <span>Edit</span>
              </Button>
            ) : (
              // Read-only (system / marketplace): install or fork into Studio.
              <>
                <Button
                  variant="secondary"
                  className="gap-1.5"
                  disabled={forking}
                  onClick={handleFork}
                >
                  {forking && (
                    <span className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin-1" />
                  )}
                  <span>Fork</span>
                </Button>
                <Button
                  className="gap-1.5"
                  disabled={installing}
                  onClick={handleInstall}
                >
                  {installing && (
                    <span className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin-1" />
                  )}
                  Install
                </Button>
              </>
            )
          }
          eyebrow={skill?.category}
          lede={
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-2.5 font-semibold uppercase tracking-[0.12em] text-fg-faint">
                  BY
                </span>
                <ProviderBadge
                  provider={provider}
                  creatorName={creator.name}
                  creatorAvatar={creator.avatarUrl}
                />
              </div>
              <span className="w-1 h-1 rounded-full bg-border-strong" />
              <button
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-8 font-mono text-3 text-fg-muted bg-bg-muted hover:bg-border-default transition-colors cursor-pointer"
                onClick={copyCommand}
                title="Copy command"
              >
                <span className="text-fg-subtle">/</span>
                {computedSlug}
                <span className="text-fg-faint ml-0.5">
                  {copied ? <SkillCheckSmallIcon /> : <SkillCopyIcon />}
                </span>
              </button>
            </div>
          }
          mainWrapperClass="items-start"
        />
      </header>

      <div className="flex flex-col gap-y-6 animate-skill-enter-2 [animation-delay:50ms]">
        {/* ── File Explorer ── */}
        <div className="flex flex-1 min-h-0 min-w-0 max-md:flex-col animate-skill-enter-2 [animation-delay:100ms]">
          {/* Tree panel (left / top on mobile) */}
          <div className="shrink-0 w-55 overflow-y-auto overflow-x-hidden bg-bg-subtle max-md:w-full max-md:max-h-55 max-sm:max-h-45">
            {/* Folder header */}
            <div className="flex items-center gap-2 px-3 pt-4 pb-2 text-3 font-semibold text-fg-default">
              <FolderIcon open />
              <span className="truncate">{computedSlug}/</span>
            </div>

            {renderTreeItems()}
          </div>

          {/* Content panel (right / bottom on mobile) */}
          <div className="flex-1 min-w-0 [overflow-wrap:break-word] [word-break:break-word] p-5 max-md:p-4 max-sm:p-3">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-3 text-fg-subtle mb-4">
              <span>{computedSlug}</span>
              {breadcrumbParts.map((part, i) => (
                <span key={`${part}-${i}`} className="flex items-center gap-1">
                  <span>/</span>
                  <span
                    className={
                      i === breadcrumbParts.length - 1
                        ? "text-fg-muted font-medium"
                        : ""
                    }
                  >
                    {part}
                  </span>
                </span>
              ))}
            </div>

            {renderContentPanel()}
          </div>
        </div>

        {/* ── Error ── */}
        {error && <p className="text-3.5 text-status-error">{error}</p>}

        {/* ── Delete (edit mode) ── */}
        {editing && editable && !isNew && (
          <div className="pt-5 border-t border-border-subtle">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="danger"
                  disabled={saving || deleting}
                  className="gap-1.5"
                >
                  {deleting ? (
                    <span className="w-3 h-3 border-2 border-status-error/30 border-t-status-error rounded-full animate-spin-1" />
                  ) : (
                    <SkillTrashIcon className="w-3" />
                  )}
                  Delete Skill
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Skill</AlertDialogTitle>
                  <AlertDialogDescription>
                    Delete "{displayedName}" permanently? This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}
